import "./apex25-chrome.css";

import { type ReactNode, useContext } from "react";

import { ctaDestination, trackEvent } from "@/lib/analytics/tracking";
import {
  APEX25_ORIGIN,
  apex25HomeUrl,
  apex25LegalUrl,
  demoBookingUrl,
  toApex25AppUrl,
} from "@/lib/apex25AppUrl";

import { HeaderAuthSlot } from "./header-auth-slot";
import { APEX25_INDUSTRY_PAGES } from "./industries";

/* Header and footer shared by the Apex 25 home page and its industry pages (tryapex25.com). On
   the home page section links are in-page anchors; on other pages they point back at the home
   page. "Book a demo" opens the cal.com booking page in a new tab, carrying the visitor's UTMs. */

export const APEX25_CONTACT_EMAIL = "mailto:apex25@theresume.io";

// The blog is served by the CMS at tryapex25.com/blog in production only (the load balancer routes
// /blog there), so it is always linked absolutely.
export const APEX25_BLOG_URL = `${APEX25_ORIGIN}/blog`;

// Off the home page, section anchors point at the Apex 25 home. That is tryapex25.com in
// production, including when the chrome is shown on recruit.theresume.io (the demo results).
const href = (target: string, onHome: boolean) =>
  target.startsWith("#") && !onHome ? `${apex25HomeUrl()}${target}` : target;

// Height of the sticky site header, plus a little air.
const HEADER_OFFSET = 88;

/**
 * Scrolls to a section of the home page. The live demo (#try-it) is centred in the space below the
 * header, or pinned just under the header when it is taller than the window. Returns false when the
 * section isn't on this page.
 */
export const scrollToSection = (id: string): boolean => {
  const section = document.getElementById(id);
  if (!section) return false;
  const target =
    id === "try-it" ? section.querySelector<HTMLElement>(".a25-demo") ?? section : section;
  const rect = target.getBoundingClientRect();
  let offset = HEADER_OFFSET;
  if (id === "try-it") {
    const room = window.innerHeight - HEADER_OFFSET;
    offset = HEADER_OFFSET + Math.max((room - rect.height) / 2, 0);
  }
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({
    top: Math.max(rect.top + window.scrollY - offset, 0),
    behavior: reduced ? "auto" : "smooth",
  });
  // Keep the address shareable without a jump. Built from the path because the page's
  // <base href="/"> would otherwise turn "#try-it" into "/#try-it".
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}#${id}`
  );
  return true;
};

/**
 * Props for an in-page link on the home page ("#try-it", "#faq"...). A plain "#x" href would
 * resolve against <base href="/"> and leave /apex25 (staging, localhost) for "/", so on the home
 * page the link scrolls instead; elsewhere it points at the Apex 25 home.
 */
export const sectionLinkProps = (target: string, onHome: boolean) => {
  if (!target.startsWith("#") || !onHome) return { href: href(target, onHome) };
  const id = target.slice(1);
  return {
    href: typeof window === "undefined" ? target : `${window.location.pathname}${target}`,
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (scrollToSection(id)) event.preventDefault();
    },
  };
};

/** Props for every "Book a demo" link; `location` says where on the page it was clicked. */
export const bookingLinkProps = (location: string) => {
  const href = demoBookingUrl();
  return {
    href,
    target: "_blank",
    rel: "noopener",
    onClick: () =>
      trackEvent("cta_clicked", {
        cta_id: "book_demo",
        cta_location: `apex25_${location}`,
        destination: ctaDestination(href),
      }),
  };
};

const Logo = ({ onHome }: { onHome: boolean }) => (
  <a
    className="brand-logo"
    {...(onHome ? sectionLinkProps("#top", true) : { href: apex25HomeUrl() })}
    aria-label="Apex 25 home"
  >
    <img src="/brand/apex25/logo.png" alt="Apex 25" height={30} />
  </a>
);

const trackAuthLink = (ctaId: string, destination: string) => () =>
  trackEvent("cta_clicked", {
    cta_id: ctaId,
    cta_location: "apex25_header",
    destination: ctaDestination(destination),
  });

/**
 * "Log in" and "Sign up" on recruit.theresume.io. A visitor who is already signed in there goes
 * straight through the sign-in page to their dashboard.
 */
const SignedOutLinks = () => {
  const login = toApex25AppUrl("/sign-in");
  const signup = toApex25AppUrl("/sign-up");
  return (
    <>
      <a className="login" href={login} onClick={trackAuthLink("login", login)}>
        Log in
      </a>
      <a className="signup" href={signup} onClick={trackAuthLink("signup", signup)}>
        Sign up
      </a>
    </>
  );
};

/**
 * tryapex25.com has no Clerk, so it can't know whether the visitor is signed in and always offers
 * "Log in / Sign up". On recruit.theresume.io (and staging, localhost) the app fills the slot and
 * the header follows the sign-in state: "Account" (dashboard, log out) once signed in.
 */
const HeaderAuth = ({ account }: { account?: ReactNode }) => {
  const AppAuth = useContext(HeaderAuthSlot);
  if (account) return <>{account}</>;
  if (!AppAuth) return <SignedOutLinks />;
  return <AppAuth signedOut={<SignedOutLinks />} />;
};

type HeaderProps = {
  onHome: boolean;
  /** Replaces the sign-in area, e.g. a page that already knows the visitor is signed in. */
  account?: ReactNode;
};

export const Apex25Header = ({ onHome, account }: HeaderProps) => (
  <>
    <div className="top">
      Are you a candidate? <a href="https://firstround.com.au">Check out firstround.com.au</a>
    </div>
    <header className="header">
      <div className="wrap header-in">
        <Logo onHome={onHome} />
        <nav className="nav" aria-label="Primary">
          <a {...sectionLinkProps("#problem", onHome)}>Problem</a>
          <a {...sectionLinkProps("#solution", onHome)}>Solution</a>
          <a {...sectionLinkProps("#try-it", onHome)}>Try the AI interview</a>
          <a href={APEX25_BLOG_URL}>Blog</a>
        </nav>
        <div className="actions">
          <div className="auth">
            <HeaderAuth account={account} />
          </div>
          <a className="btn header-cta" {...bookingLinkProps("header")}>
            Book a Demo
          </a>
        </div>
      </div>
    </header>
  </>
);

// Only links with a real Apex 25 destination. Pricing and team pages don't exist under the
// Apex 25 brand yet; add them here once they do.
const footerColumns = [
  {
    heading: "Product",
    links: [
      { label: "Problem", href: "#problem" },
      { label: "Solution", href: "#solution" },
      // Industry pages from industries.ts, e.g. /for-legal -> "For legal teams".
      ...Object.keys(APEX25_INDUSTRY_PAGES).map((path) => ({
        label: `For ${path.replace("/for-", "")} teams`,
        href: path,
      })),
    ],
  },
  {
    heading: "Workflow",
    links: [
      { label: "How It Works", href: "#how-it-works" },
      { label: "FAQ", href: "#faq" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "Blog", href: APEX25_BLOG_URL },
      { label: "Contact Us", href: APEX25_CONTACT_EMAIL },
    ],
  },
  {
    heading: "For Candidates",
    links: [{ label: "Try First Round", href: "https://firstround.com.au" }],
  },
];

// The legal set (/legal is the hub). Paths resolve per environment by apex25LegalUrl.
const legalLinks = [
  { label: "Terms of Service", path: "/terms" },
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Candidate terms", path: "/candidate-terms" },
  { label: "Trial terms", path: "/trial-terms" },
  { label: "All legal documents", path: "/legal" },
];

export const Apex25Footer = ({ onHome }: { onHome: boolean }) => (
  <footer>
    <div className="wrap footer-grid">
      <div>
        <Logo onHome={onHome} />
        <p style={{ color: "var(--muted)", fontSize: 14 }}>
          Intelligence-first recruiting for modern teams.
        </p>
        <p className="powered-by">Powered by theResume</p>
      </div>
      {footerColumns.map((column) => (
        <div key={column.heading}>
          <h4>{column.heading}</h4>
          <ul>
            {column.links.map((link) => (
              <li key={link.label}>
                <a {...sectionLinkProps(link.href, onHome)}>{link.label}</a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="legal">
      <div className="wrap legal-row">
        <span>© 2026 Apex 25. Crafted with precision.</span>
        <nav aria-label="Legal">
          <ul className="legal-links">
            {legalLinks.map((link) => (
              <li key={link.path}>
                <a href={apex25LegalUrl(link.path)}>{link.label}</a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  </footer>
);
